import React, { useEffect } from "react";
import {
  Box, Button, Card, Typography, Chip,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, CircularProgress, Skeleton,
  Snackbar, Alert, InputAdornment, Tooltip,
  Table, TableHead, TableRow, TableCell, TableBody,
  TablePagination,
} from "@mui/material";
import {
  Plus, Pencil, Trash2, X, Calendar, Clock, MapPin, Link2, Users,
  Search, BookOpen, Globe as GlobeIcon,
} from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm, Controller } from "react-hook-form";
import dayjs from "dayjs";
import AdminShell from "../layout/AdminShell";
import {
  adminGetEvents,
  adminGetEvent,
  adminCreateEvent,
  adminUpdateEvent,
  adminDeleteEvent,
} from "../../api/events";
import { adminGetCourses } from "../../api/courses";
import { useNavigate } from "react-router-dom";

// NOTE: The Event model on the backend only has these real fields:
// resource, start_datetime, end_datetime, location, meeting_link,
// is_registration_open, max_registrations (+ read-only resource_title,
// resource_slug, registration_count, id, created_at, updated_at).
// There is NO title / mode / status / event_date field on the backend,
// so those are derived client-side for display only and are never sent
// in the create/update payload.

const STATUS_STYLES = {
  UPCOMING:  { bg: "rgba(37,99,235,0.1)",   color: "#2563EB" },
  ONGOING:   { bg: "rgba(34,197,94,0.1)",   color: "#16A34A" },
  COMPLETED: { bg: "rgba(107,114,128,0.1)", color: "#475569" },
  CANCELLED: { bg: "rgba(239,68,68,0.1)",   color: "#DC2626" },
};

const MODE_STYLES = {
  ONLINE:  { bg: "rgba(6,182,212,0.08)",   color: "#0891B2" },
  OFFLINE: { bg: "rgba(34,197,94,0.08)",   color: "#16A34A" },
  HYBRID:  { bg: "rgba(139,92,246,0.08)",  color: "#7C3AED" },
};

const DEFAULT_VALUES = {
  resource: "",
  start_datetime: "",
  end_datetime: "",
  location: "",
  meeting_link: "",
  is_registration_open: true,
  max_registrations: 0,
};

// datetime-local inputs need "YYYY-MM-DDTHH:mm", not a raw ISO string with seconds/Z
const toLocalInput = (value) =>
  value && dayjs(value).isValid() ? dayjs(value).format("YYYY-MM-DDTHH:mm") : "";

function extractErrorMessage(err, fallback) {
  const data = err?.response?.data;
  if (data && typeof data === "object") {
    const first = Object.values(data)[0];
    return Array.isArray(first) ? first[0] : String(first);
  }
  return fallback;
}

export default function EventsPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(false);
  const [editing, setEditing] = React.useState(null);
  const [confirmDelete, setConfirmDelete] = React.useState(null);
  const [snackbar, setSnackbar] = React.useState({ open: false, message: "", severity: "success" });
  const [searchQuery, setSearchQuery] = React.useState("");
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  const { control, handleSubmit, reset, formState: { errors } } = useForm({
    defaultValues: DEFAULT_VALUES,
  });

  const notify = (message, severity = "success") =>
    setSnackbar({ open: true, message, severity });

  // Queries
  const { data: events = [], isLoading } = useQuery({
    queryKey: ["admin-events"],
    queryFn: () => adminGetEvents().then(r => r.data),
  });

  const { data: courses = [], isLoading: coursesLoading } = useQuery({
    queryKey: ["admin-courses"],
    queryFn: () => adminGetCourses().then(r => r.data),
  });

  // Fetch single event for edit (so we get the real `resource` id, not just resource_title)
  const { data: editEventData, isLoading: isLoadingEdit } = useQuery({
    queryKey: ["admin-event", editing?.id],
    queryFn: () => adminGetEvent(editing?.id).then(r => r.data),
    enabled: !!editing?.id && open,
    staleTime: 0,
  });

  useEffect(() => {
    if (editEventData && editing) {
      reset({
        resource: editEventData.resource ?? "",
        start_datetime: toLocalInput(editEventData.start_datetime),
        end_datetime: toLocalInput(editEventData.end_datetime),
        location: editEventData.location || "",
        meeting_link: editEventData.meeting_link || "",
        is_registration_open: editEventData.is_registration_open ?? true,
        max_registrations: editEventData.max_registrations ?? 0,
      });
    }
  }, [editEventData, editing, reset]);

  const createMutation = useMutation({
    mutationFn: adminCreateEvent,
    onSuccess: () => {
      qc.invalidateQueries(["admin-events"]);
      notify("Event created");
      closeDialog();
    },
    onError: (err) => notify(extractErrorMessage(err, "Couldn't create event"), "error"),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => adminUpdateEvent(id, data),
    onSuccess: () => {
      qc.invalidateQueries(["admin-events"]);
      qc.invalidateQueries(["admin-event", editing?.id]);
      notify("Event updated");
      closeDialog();
    },
    onError: (err) => notify(extractErrorMessage(err, "Couldn't update event"), "error"),
  });

  const deleteMutation = useMutation({
    mutationFn: adminDeleteEvent,
    onSuccess: () => {
      qc.invalidateQueries(["admin-events"]);
      notify("Event deleted");
      setConfirmDelete(null);
    },
    onError: (err) => {
      notify(extractErrorMessage(err, "Couldn't delete event"), "error");
      setConfirmDelete(null);
    },
  });

  const saving = createMutation.isPending || updateMutation.isPending;

  // Form submit — only send fields the backend actually accepts
  const onSubmit = (values) => {
    const payload = {
      resource: Number(values.resource),
      start_datetime: values.start_datetime ? dayjs(values.start_datetime).toISOString() : null,
      end_datetime: values.end_datetime ? dayjs(values.end_datetime).toISOString() : null,
      location: values.location,
      meeting_link: values.meeting_link,
      is_registration_open: values.is_registration_open,
      max_registrations: values.max_registrations || 0,
    };
    if (editing) {
      updateMutation.mutate({ id: editing.id, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  // Dialog helpers
  const openCreate = () => {
    setEditing(null);
    reset(DEFAULT_VALUES);
    setOpen(true);
  };

  const openEdit = (event) => {
    setEditing(event);
    setOpen(true);
  };

  const closeDialog = () => {
    if (saving) return;
    setOpen(false);
    setEditing(null);
    reset(DEFAULT_VALUES);
  };

  const viewRegistrations = (eventId) => {
    navigate(`/admin/events/${eventId}/registrations`);
  };

  // Search and pagination
  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setPage(0);
  };
  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const filteredEvents = events.filter(e =>
    e.resource_title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    e.location?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const startIndex = page * rowsPerPage;
  const paginatedEvents = filteredEvents.slice(startIndex, startIndex + rowsPerPage);

  // Status/mode aren't real backend fields — derive them for display only
  const getEventStatus = (event) => {
    const now = dayjs();
    const start = dayjs(event.start_datetime);
    const end = dayjs(event.end_datetime);
    if (now.isBefore(start)) return "UPCOMING";
    if (now.isAfter(end)) return "COMPLETED";
    return "ONGOING";
  };

  const getEventMode = (event) => {
    if (event.meeting_link && event.location) return "HYBRID";
    if (event.meeting_link) return "ONLINE";
    if (event.location) return "OFFLINE";
    return "ONLINE";
  };

  return (
    <AdminShell title="Events">
      {/* Page header */}
      <Box sx={{ display: "flex", alignItems: { xs: "flex-start", sm: "center" }, justifyContent: "space-between", flexDirection: { xs: "column", sm: "row" }, gap: 1.5, mb: 2.5 }}>
        <Typography sx={{ fontSize: 13.5, color: "text.secondary", maxWidth: 460 }}>
          Manage events, view registrations, and update event details.
        </Typography>
        <Button
          variant="contained"
          startIcon={<Plus size={18} />}
          onClick={openCreate}
          sx={{
            borderRadius: "10px", textTransform: "none", fontWeight: 700,
            bgcolor: "#2563EB", boxShadow: "0 10px 24px -12px rgba(37,99,235,0.5)",
            flexShrink: 0, "&:hover": { bgcolor: "#1D4ED8" },
          }}
        >
          Add Event
        </Button>
      </Box>

      {/* Search bar */}
      <Card sx={{ borderRadius: "16px", border: "1px solid rgba(15,23,42,0.08)", boxShadow: "0 1px 2px rgba(15,23,42,0.04)", overflow: "hidden", mb: 2.5 }}>
        <Box sx={{ p: 2, display: "flex", alignItems: "center", gap: 1.5 }}>
          <Search size={18} color="#94A3B8" />
          <TextField
            fullWidth
            placeholder="Search events by title or location..."
            value={searchQuery}
            onChange={handleSearch}
            size="small"
            InputProps={{ sx: { fontSize: 13.5 } }}
          />
        </Box>
      </Card>

      {/* Table */}
      <Card sx={{ borderRadius: "16px", border: "1px solid rgba(15,23,42,0.08)", boxShadow: "0 1px 2px rgba(15,23,42,0.04)", overflow: "hidden" }}>
        <Box sx={{ overflowX: "auto" }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: "#F8FAFC" }}>
                {["Event", "Date & Time", "Mode", "Registrations", "Status", ""].map(h => (
                  <TableCell key={h} sx={{ fontWeight: 700, fontSize: 12, letterSpacing: "0.03em", textTransform: "uppercase", color: "text.secondary", borderBottom: "1px solid rgba(15,23,42,0.08)", whiteSpace: "nowrap" }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 6 }).map((__, j) => (
                      <TableCell key={j}><Skeleton variant="text" width={j === 0 ? 200 : j === 1 ? 150 : 80} /></TableCell>
                    ))}
                  </TableRow>
                ))
              ) : paginatedEvents.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                    <Box sx={{ width: 52, height: 52, borderRadius: "14px", bgcolor: "rgba(37,99,235,0.07)", display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 1.5 }}>
                      <Calendar size={22} color="#2563EB" />
                    </Box>
                    <Typography fontWeight={700} color="#0F172A" mb={0.5}>
                      {searchQuery ? "No matching events found" : "No events yet"}
                    </Typography>
                    <Typography color="text.secondary" fontSize={13.5}>
                      {searchQuery ? "Try adjusting your search." : "Create an event to get started."}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : paginatedEvents.map((e) => {
                const status = getEventStatus(e);
                const mode = getEventMode(e);
                const statusStyle = STATUS_STYLES[status] || STATUS_STYLES.UPCOMING;
                const modeStyle = MODE_STYLES[mode] || MODE_STYLES.ONLINE;

                return (
                  <TableRow key={e.id} hover sx={{ "&:last-child td": { borderBottom: 0 } }}>
                    {/* Event */}
                    <TableCell sx={{ minWidth: 220 }}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 700, fontSize: 13.75, color: "#0F172A", lineHeight: 1.3 }} noWrap>
                          {e.resource_title || "Untitled Event"}
                        </Typography>
                        {e.location && (
                          <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 0.2, display: "flex", alignItems: "center", gap: 0.5 }} noWrap>
                            <MapPin size={12} />
                            {e.location}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>

                    {/* Date & Time */}
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                        {e.start_datetime && (
                          <Chip
                            label={dayjs(e.start_datetime).format("DD MMM")}
                            size="small"
                            sx={{ fontWeight: 600, fontSize: 11, height: 20, bgcolor: "rgba(37,99,235,0.07)", color: "#2563EB" }}
                          />
                        )}
                        {e.start_datetime && e.end_datetime && (
                          <Chip
                            label={dayjs(e.start_datetime).format("hh:mm A")}
                            size="small"
                            sx={{ fontWeight: 600, fontSize: 11, height: 20, bgcolor: "rgba(107,114,128,0.07)", color: "#475569" }}
                          />
                        )}
                      </Box>
                    </TableCell>

                    {/* Mode (derived, display-only) */}
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      <Chip
                        label={mode}
                        size="small"
                        sx={{ fontWeight: 700, fontSize: 11, height: 22, bgcolor: modeStyle.bg, color: modeStyle.color }}
                      />
                    </TableCell>

                    {/* Registrations */}
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                        <Users size={14} color="#94A3B8" />
                        <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: "#0F172A" }}>
                          {e.registration_count ?? 0}/{e.max_registrations || "∞"}
                        </Typography>
                      </Box>
                    </TableCell>

                    {/* Status (derived, display-only) */}
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      <Chip
                        label={status}
                        size="small"
                        sx={{ fontWeight: 700, fontSize: 11, height: 22, bgcolor: statusStyle.bg, color: statusStyle.color }}
                      />
                    </TableCell>

                    {/* Actions */}
                    <TableCell align="right">
                      <Box sx={{ display: "flex", gap: 0.75, justifyContent: "flex-end" }}>
                        <Tooltip title="View Registrations">
                          <IconButton
                            size="small"
                            onClick={() => viewRegistrations(e.id)}
                            sx={{
                              border: "1px solid rgba(15,23,42,0.1)",
                              borderRadius: "8px",
                              "&:hover": { borderColor: "#06B6D4", color: "#0891B2", bgcolor: "rgba(6,182,212,0.05)" }
                            }}
                          >
                            <Users size={14} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => openEdit(e)}
                            disabled={saving}
                            sx={{
                              border: "1px solid rgba(15,23,42,0.1)",
                              borderRadius: "8px",
                              "&:hover": { borderColor: "#2563EB", color: "#2563EB", bgcolor: "rgba(37,99,235,0.05)" }
                            }}
                          >
                            <Pencil size={14} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => setConfirmDelete(e)}
                            sx={{
                              border: "1px solid rgba(15,23,42,0.1)",
                              borderRadius: "8px",
                              "&:hover": { borderColor: "#EF4444", color: "#EF4444", bgcolor: "rgba(239,68,68,0.05)" }
                            }}
                          >
                            <Trash2 size={14} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>

        {/* Pagination */}
        {!isLoading && filteredEvents.length > 0 && (
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            component="div"
            count={filteredEvents.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              borderTop: "1px solid rgba(15,23,42,0.08)",
              "& .MuiTablePagination-toolbar": { pl: 2, pr: 1 },
              "& .MuiTablePagination-selectLabel": { fontSize: 13.5, color: "text.secondary" },
              "& .MuiTablePagination-displayedRows": { fontSize: 13.5, color: "text.secondary" },
            }}
          />
        )}
      </Card>

      {/* Create / Edit dialog */}
      <Dialog
        open={open}
        onClose={closeDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: "18px", overflow: "hidden" } }}
      >
        <Box sx={{
          background: "linear-gradient(135deg, #1D4ED8 0%, #2563EB 50%, #3B82F6 100%)",
          px: 3, py: 2.5,
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: 18, color: "white" }}>
              {editing ? "Edit Event" : "Add New Event"}
            </Typography>
            <Typography sx={{ fontSize: 13, color: "rgba(255,255,255,0.85)", fontWeight: 400, mt: 0.25 }}>
              {editing ? "Update event details and settings." : "Attach an event to a course or resource."}
            </Typography>
          </Box>
          <IconButton size="small" onClick={closeDialog} disabled={saving} sx={{ color: "rgba(255,255,255,0.85)" }}>
            <X size={20} />
          </IconButton>
        </Box>

        <Box component="form" onSubmit={handleSubmit(onSubmit)}>
          <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2.5, pt: 2.5 }}>
            {isLoadingEdit ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <>
                {/* Event Information Card */}
                <Card sx={{ borderRadius: "12px", border: "1px solid rgba(15,23,42,0.08)", boxShadow: "0 1px 2px rgba(15,23,42,0.04)", p: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 2 }}>
                    <Calendar size={16} color="#2563EB" />
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#0F172A", letterSpacing: "0.05em", textTransform: "uppercase" }}>
                      Event Information
                    </Typography>
                  </Box>
                  <Box sx={{ display: "grid", gridTemplateColumns: "repeat(1, 1fr)", gap: 2 }}>
                    <Controller
                      name="resource"
                      control={control}
                      rules={{ required: "A course/resource is required" }}
                      render={({ field, fieldState }) => (
                        <TextField
                          {...field}
                          select
                          label="Course / Resource"
                          fullWidth
                          disabled={saving || coursesLoading}
                          error={!!fieldState.error}
                          helperText={fieldState.error?.message || (coursesLoading ? "Loading courses..." : "The event will display under this course")}
                          InputProps={{
                            startAdornment: <InputAdornment position="start"><BookOpen size={15} color="#94A3B8" /></InputAdornment>
                          }}
                        >
                          {courses.map(c => (
                            <MenuItem key={c.id} value={c.id}>{c.title}</MenuItem>
                          ))}
                        </TextField>
                      )}
                    />
                    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
                      <Controller
                        name="start_datetime"
                        control={control}
                        rules={{ required: "Start time is required" }}
                        render={({ field, fieldState }) => (
                          <TextField
                            {...field}
                            label="Start Time"
                            fullWidth
                            disabled={saving}
                            type="datetime-local"
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message}
                            InputLabelProps={{ shrink: true }}
                            InputProps={{
                              startAdornment: <InputAdornment position="start"><Clock size={15} color="#94A3B8" /></InputAdornment>
                            }}
                          />
                        )}
                      />
                      <Controller
                        name="end_datetime"
                        control={control}
                        rules={{ required: "End time is required" }}
                        render={({ field, fieldState }) => (
                          <TextField
                            {...field}
                            label="End Time"
                            fullWidth
                            disabled={saving}
                            type="datetime-local"
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message}
                            InputLabelProps={{ shrink: true }}
                            InputProps={{
                              startAdornment: <InputAdornment position="start"><Clock size={15} color="#94A3B8" /></InputAdornment>
                            }}
                          />
                        )}
                      />
                    </Box>
                  </Box>
                </Card>

                {/* Mode & Location Card */}
                <Card sx={{ borderRadius: "12px", border: "1px solid rgba(15,23,42,0.08)", boxShadow: "0 1px 2px rgba(15,23,42,0.04)", p: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 2 }}>
                    <MapPin size={16} color="#2563EB" />
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#0F172A", letterSpacing: "0.05em", textTransform: "uppercase" }}>
                      Location
                    </Typography>
                  </Box>
                  <Box sx={{ display: "grid", gridTemplateColumns: "repeat(1, 1fr)", gap: 2 }}>
                    <Controller
                      name="location"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Location"
                          fullWidth
                          disabled={saving}
                          placeholder="e.g. Conference Hall A"
                          InputProps={{
                            startAdornment: <InputAdornment position="start"><MapPin size={15} color="#94A3B8" /></InputAdornment>
                          }}
                        />
                      )}
                    />
                    <Controller
                      name="meeting_link"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Meeting Link"
                          fullWidth
                          disabled={saving}
                          placeholder="https://meet.example.com/room-id"
                          InputProps={{
                            startAdornment: <InputAdornment position="start"><Link2 size={15} color="#94A3B8" /></InputAdornment>
                          }}
                        />
                      )}
                    />
                    <Typography sx={{ fontSize: 12, color: "text.secondary", display: "flex", alignItems: "center", gap: 0.5 }}>
                      <GlobeIcon size={12} /> Fill location for in-person, meeting link for online, or both for hybrid.
                    </Typography>
                  </Box>
                </Card>

                {/* Registration Settings Card */}
                <Card sx={{ borderRadius: "12px", border: "1px solid rgba(15,23,42,0.08)", boxShadow: "0 1px 2px rgba(15,23,42,0.04)", p: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 2 }}>
                    <Users size={16} color="#2563EB" />
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#0F172A", letterSpacing: "0.05em", textTransform: "uppercase" }}>
                      Registration Settings
                    </Typography>
                  </Box>
                  <Box sx={{ display: "grid", gridTemplateColumns: "repeat(1, 1fr)", gap: 2 }}>
                    <Controller
                      name="is_registration_open"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          select
                          label="Registration Status"
                          fullWidth
                          disabled={saving}
                          InputProps={{
                            startAdornment: <InputAdornment position="start"><Users size={15} color="#94A3B8" /></InputAdornment>
                          }}
                        >
                          <MenuItem value={true}>Open</MenuItem>
                          <MenuItem value={false}>Closed</MenuItem>
                        </TextField>
                      )}
                    />
                    <Controller
                      name="max_registrations"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Maximum Registrations"
                          fullWidth
                          disabled={saving}
                          type="number"
                          placeholder="0 for unlimited"
                          inputProps={{ min: 0, step: 1 }}
                          onChange={(e) => field.onChange(e.target.value === "" ? 0 : Number(e.target.value))}
                          InputProps={{
                            startAdornment: <InputAdornment position="start"><Users size={15} color="#94A3B8" /></InputAdornment>
                          }}
                        />
                      )}
                    />
                  </Box>
                </Card>
              </>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
            <Button onClick={closeDialog} disabled={saving} sx={{ textTransform: "none", fontWeight: 600, color: "text.secondary" }}>Cancel</Button>
            <Button
              type="submit"
              variant="contained"
              disabled={saving || isLoadingEdit}
              startIcon={saving ? <CircularProgress size={15} color="inherit" /> : null}
              sx={{
                textTransform: "none", fontWeight: 700, borderRadius: "10px",
                bgcolor: "#2563EB", px: 2.5, minWidth: 100,
                "&:hover": { bgcolor: "#1D4ED8" }
              }}
            >
              {saving ? "Saving..." : editing ? "Update" : "Create"}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog
        open={!!confirmDelete}
        onClose={() => !deleteMutation.isPending && setConfirmDelete(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "18px" } }}
      >
        <DialogTitle sx={{ pb: 1, borderBottom: "1px solid rgba(15,23,42,0.08)" }}>
          <Typography sx={{ fontWeight: 700, fontSize: 17 }}>Delete this event?</Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography sx={{ fontSize: 13.75, color: "text.secondary" }}>
            "{confirmDelete?.resource_title || "This event"}" and its registrations will be removed. This can't be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, borderTop: "1px solid rgba(15,23,42,0.08)" }}>
          <Button
            onClick={() => setConfirmDelete(null)}
            disabled={deleteMutation.isPending}
            sx={{ textTransform: "none", fontWeight: 600, color: "text.secondary" }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => deleteMutation.mutate(confirmDelete.id)}
            disabled={deleteMutation.isPending}
            variant="contained"
            startIcon={deleteMutation.isPending ? <CircularProgress size={15} color="inherit" /> : null}
            sx={{
              textTransform: "none", fontWeight: 700, borderRadius: "10px",
              bgcolor: "#EF4444", px: 2.5, minWidth: 100,
              "&:hover": { bgcolor: "#DC2626" }
            }}
          >
            {deleteMutation.isPending ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={3500} onClose={() => setSnackbar(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
        <Alert onClose={() => setSnackbar(s => ({ ...s, open: false }))} severity={snackbar.severity} variant="filled" sx={{ borderRadius: "10px", fontWeight: 600, fontSize: 13.5 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </AdminShell>
  );
}